import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  disabled?: boolean;
  clearable?: boolean;
  clearLabel?: string;
  emptyMessage?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Wybierz...',
  searchPlaceholder = 'Szukaj...',
  id,
  disabled = false,
  clearable = false,
  clearLabel = '— brak —',
  emptyMessage = 'Brak wyników',
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
    width: number;
    placement: 'bottom' | 'top';
  } | null>(null);

  // Pick portal target: closest [role="dialog"] (so Radix FocusScope considers
  // our popover part of the dialog scope), otherwise document.body.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const dialog = triggerRef.current.closest<HTMLElement>('[role="dialog"]');
    setPortalTarget(dialog ?? document.body);
  }, [open]);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = popoverRef.current?.offsetHeight ?? 290;
    // Decide placement based on available room inside the portal target
    // (dialog if any, else viewport).
    let containerTop = 0;
    let containerLeft = 0;
    let containerBottom = window.innerHeight;
    if (portalTarget && portalTarget !== document.body) {
      const r = portalTarget.getBoundingClientRect();
      containerTop = r.top;
      containerLeft = r.left;
      containerBottom = r.bottom;
    }
    const roomBelow = containerBottom - triggerRect.bottom;
    const roomAbove = triggerRect.top - containerTop;
    const placeAbove = roomBelow < estimatedHeight && roomAbove > roomBelow;
    setPopoverPos({
      top: placeAbove
        ? triggerRect.top - containerTop - estimatedHeight - 4
        : triggerRect.bottom - containerTop + 4,
      left: triggerRect.left - containerLeft,
      width: triggerRect.width,
      placement: placeAbove ? 'top' : 'bottom',
    });
  };

  useLayoutEffect(() => {
    if (!open || !portalTarget) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portalTarget]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
        setSearchQuery('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const filteredOptions = searchQuery
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : options;

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={`truncate text-left ${
            !selectedOption ? 'text-muted-foreground' : ''
          }`}
        >
          {displayLabel}
        </span>
        <div className="flex items-center gap-1">
          {clearable && value && !disabled && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="rounded-sm hover:bg-accent p-0.5"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </button>
      {open &&
        popoverPos &&
        portalTarget &&
        createPortal(
          <div
            ref={popoverRef}
            data-searchable-popover=""
            style={{
              position: 'absolute',
              top: popoverPos.top,
              left: popoverPos.left,
              width: popoverPos.width,
              zIndex: 60,
              pointerEvents: 'auto',
            }}
            className="rounded-md border bg-popover shadow-md"
          >
            <div className="p-1 border-b">
              <div className="flex items-center gap-2 px-2">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full py-1 text-sm bg-transparent outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="p-1 max-h-60 overflow-y-auto">
              {clearable && (
                <button
                  type="button"
                  onClick={() => handleSelect('')}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer text-muted-foreground"
                >
                  <div className="w-4 shrink-0" />
                  {clearLabel}
                </button>
              )}
              {filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                >
                  <div className="w-4 shrink-0 flex items-center justify-center">
                    {value === option.value && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                  {option.label}
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                  {emptyMessage}
                </p>
              )}
            </div>
          </div>,
          portalTarget,
        )}
    </div>
  );
};
